import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Account, AccountBalance, Program } from '@monorepo/types';
import { usePerformanceChartData } from '../../../../hooks';
import { DEFAULT_FILTER } from '../constants';
import {
  FilterOption,
  FilterType,
  UsePerformanceChartDataReturn,
} from '../types';
import { transformChartData } from '../utils';

/**
 * Custom hook that manages performance chart logic
 */
export const usePerformanceChartLogic = (
  account: Account | null,
  accountBalance?: AccountBalance | null,
  program?: Program | null,
): UsePerformanceChartDataReturn => {
  const { t } = useTranslation('performanceChart');
  const [selectedFilter, setSelectedFilter] =
    useState<FilterType>(DEFAULT_FILTER);

  const {
    data: balanceChartData,
    isLoading,
    isError,
    refetch,
  } = usePerformanceChartData(account?.tsAccountId, account || undefined);

  // Create filter options with translations
  const filterOptions: FilterOption[] = useMemo(
    () => [
      { label: t('filters.all'), value: 'all' },
      { label: t('filters.weekly'), value: 'weekly' },
      { label: t('filters.monthly'), value: 'monthly' },
      { label: t('filters.yearly'), value: 'yearly' },
    ],
    [t],
  );

  // Transform chart data based on current filter
  const { chartData, xLabels, yAxisDomain } = useMemo(() => {
    if (!balanceChartData || balanceChartData.length === 0 || !account) {
      return { chartData: [], xLabels: [], yAxisDomain: undefined };
    }

    const baseBalance = program?.config?.buyingPower
      ? Number(program.config.buyingPower)
      : accountBalance?.baseBalance || 0;
    return transformChartData(
      balanceChartData,
      baseBalance,
      account.createdAt,
      selectedFilter,
    );
  }, [
    balanceChartData,
    program?.config?.buyingPower,
    accountBalance?.balance,
    account,
    selectedFilter,
  ]);

  const onFilterChange = (filter: FilterType): void => {
    setSelectedFilter(filter);
  };

  return {
    chartData,
    xLabels,
    yAxisDomain,
    selectedFilter,
    filterOptions,
    onFilterChange,
    isLoading,
    isError,
    refetch,
  };
};
